import { useState } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Images } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { ServicePreview } from "./ServicePreview";
import { imgUrl } from "@/lib/api";

const Row = ({ s, i, onBuy, onHover, onPreview }) => (
  <Reveal delay={Math.min(i * 0.04, 0.3)}>
    <div
      onMouseEnter={() => onHover(s)}
      onMouseLeave={() => onHover(null)}
      data-testid={`service-row-${i}`}
      className="group grid grid-cols-12 items-center gap-4 border-b border-[#163D43] py-7 transition-colors hover:bg-[#0B2326]/60 md:px-4"
    >
      <span className="font-mono-x col-span-2 text-sm text-[#2DD4BF] md:col-span-1">{String(i + 1).padStart(2, "0")}</span>
      <div className="col-span-10 md:col-span-6">
        <h3 className="font-display text-2xl font-semibold text-teal-50 transition-transform duration-500 group-hover:translate-x-2 md:text-4xl">{s.title}</h3>
        <p className="mt-2 text-sm text-teal-200/90 md:text-base">{s.description}</p>
        {s.gallery?.length > 0 && (
          <button onClick={() => onPreview(s)} data-testid={`service-preview-${i}`} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#2DD4BF] underline-offset-4 hover:underline">
            <Images className="h-4 w-4" /> Ver prévias ({s.gallery.length})
          </button>
        )}
      </div>
      {s.image_url && <img src={imgUrl(s.image_url)} alt={s.title} onClick={() => s.gallery?.length && onPreview(s)} className="col-span-12 h-44 w-full rounded-sm object-cover md:hidden" />}
      <span className="font-mono-x col-span-6 text-xs uppercase tracking-[0.18em] text-teal-300 md:col-span-2">{s.category}</span>
      <div className="col-span-6 flex items-center justify-end gap-4 md:col-span-3">
        <span className="text-sm text-teal-50" data-testid={`service-price-${i}`}>{s.price || "Sob consulta"}</span>
        <button onClick={() => onBuy(s)} data-testid={`service-buy-${i}`} className="inline-flex items-center gap-2 rounded-full border border-[#2DD4BF]/60 px-5 py-2.5 text-sm font-semibold text-[#2DD4BF] transition-colors group-hover:bg-[#2DD4BF] group-hover:text-[#041214]">
          Contratar <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  </Reveal>
);

const CursorPreview = ({ item, x, y }) => (
  <motion.div style={{ x, y }} className="pointer-events-none fixed left-0 top-0 z-40 hidden md:block">
    <AnimatePresence>
      {item?.image_url && (
        <motion.img
          key={item.id}
          src={imgUrl(item.image_url)}
          alt=""
          initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          exit={{ opacity: 0, scale: 0.85 }}
          transition={{ duration: 0.35 }}
          className="absolute h-56 w-44 max-w-none -translate-x-1/2 -translate-y-1/2 rounded-sm border border-[#2DD4BF]/40 object-cover shadow-2xl"
        />
      )}
    </AnimatePresence>
  </motion.div>
);

export const Services = ({ services, onBuy }) => {
  const [hover, setHover] = useState(null);
  const [preview, setPreview] = useState(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 200, damping: 25 });
  const y = useSpring(my, { stiffness: 200, damping: 25 });
  return (
    <section id="servicos" className="relative bg-[#041214] py-24 md:py-36" onMouseMove={(e) => { mx.set(e.clientX + 120); my.set(e.clientY); }} data-testid="services-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mb-16 grid gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <Eyebrow n="01">Catálogo de serviços</Eyebrow>
            <Reveal><h2 className="font-display mt-6 text-4xl font-semibold uppercase leading-[0.95] text-teal-50 md:text-6xl">Do cartaz ao <span className="font-serif-i normal-case text-[#2DD4BF]">branding</span> completo.</h2></Reveal>
          </div>
          <Reveal className="self-end md:col-span-5"><p className="text-base leading-relaxed text-teal-100/85">Escolha o serviço, faça o pagamento via Pix e envie o comprovante e o briefing pelo WhatsApp. Simples assim.</p></Reveal>
        </div>
        <div className="border-t border-[#163D43]">
          {services.map((s, i) => <Row key={s.id} s={s} i={i} onBuy={onBuy} onHover={setHover} onPreview={setPreview} />)}
          {services.length === 0 && <p className="py-10 text-teal-200">Serviços em breve.</p>}
        </div>
      </div>
      <CursorPreview item={preview ? null : hover} x={x} y={y} />
      <ServicePreview key={preview?.id || "none"} service={preview} onClose={() => setPreview(null)} onBuy={onBuy} />
    </section>
  );
};
